/**
 * @file src/i18n/index.js
 * @description Vue-i18n configuration with lazy-loaded locale support.
 *
 * English is loaded eagerly as the default/fallback locale.
 * All other locales are loaded on demand when the user switches language.
 */
import Vue from 'vue'
import VueI18n from 'vue-i18n'
import languages from './languages'
import enLocale from '@/locales/en.json'

Vue.use(VueI18n)

/**
 * Convert flat keys like "cart.title" to nested structure { cart: { title: ... } }
 * @param {Object} flatObj - Object with dot-notation keys
 * @returns {Object} Nested object
 */
function unflatten(flatObj) {
  const result = {}
  for (const key in flatObj) {
    const keys = key.split('.')
    let current = result
    for (let i = 0; i < keys.length - 1; i++) {
      if (!current[keys[i]]) {
        current[keys[i]] = {}
      }
      current = current[keys[i]]
    }
    current[keys[keys.length - 1]] = flatObj[key]
  }
  return result
}

/**
 * Deep merge source into target (mutates target).
 * @param {Object} target
 * @param {Object} source
 * @returns {Object} Merged target
 */
function deepMerge(target, source) {
  for (const key in source) {
    if (source[key] && typeof source[key] === 'object' && !Array.isArray(source[key])) {
      if (!target[key]) target[key] = {}
      deepMerge(target[key], source[key])
    } else {
      target[key] = source[key]
    }
  }
  return target
}

// Build English messages: inline stubs merged with flat locale file
const enBase = {
  close: 'Close',
  photos: 'Photos.',
  vieworder: 'View Order',
  selectevent: 'Select an event ({count} found)',
  searchforevent: 'Search for your event',
  bibhasorder: 'This bib has an existing order',

  event: {
    bibnumber: 'Bib Number',
    enterbib: 'Enter bib number or name',
    nobibnumber: 'Enter bib number or name',
    entername: 'Enter your name',
    getnotifications: 'Get Notifications',
    yes: 'Yes',
    no: 'No',
    carterror: 'Cart Error',
    download: 'Download',
    freephotodownload: 'Free Photo Download',
    upgrade: 'Upgrade Your Pack',
    continue: 'Continue',
    free: 'Free',
    allyourphotosplus: 'All your photos plus extras',
    cert: 'Certificate',
    racetime: 'Race Time',
    commemorative: 'Commemorative',
    instantsocial: 'Instant Social',
    clickimagetopreview: 'Click image to preview',
    everythingdigital: 'Everything Digital',
    everythingdigitalabout: 'Get every digital product in one pack',
    personalvideo: 'Personal Video',
    personalvideofromall: 'A personal video from all your race photos',
    videoofyou: '{amount} videos of you',
    videoofyouone: '{amount} video of you',
    freewithallpacks: 'Free with all packs',
    viewyourphotos: 'View Your Photos',
    dl_eventcd: 'Event CD',
    dl_digsuperpack: '+ Super Pack',
    dl_megapack: '+ Mega Pack',
    $movie: 'Personal Video',
    cp: 'Commemorative Print',
    rt: 'Race Time',
    original: 'Original Photo',
    searchcolours: 'Search by colours',
    searchbyface: 'Search by face',
    searchbycolor: 'Search by colour',
    searchbytime: 'Search by time',
    searchbyswim: 'Search by swim time',
    congratulations: 'Congratulations',
    wevefound: "We've found",
    photosofyou: 'photos of you',
    photosofyousofar: 'photos of you so far',
    photos: 'Photos',
    previousyears: 'Previous Years',
    previousyearstext: 'Click a year below to view your images for that event',
    noimagesfound: 'Sorry, No images were found.',
    search: 'Search',
    digitaldownload: 'Digital Download',
    continuetocart: 'Continue to Cart',
    singlephoto: 'Single Photo',
    notme: 'Not me',
    crop: 'Crop',
    viewcrop: 'View Crop',
    clearcrop: 'Clear Crop',
    loading: 'Loading...',
  },

  forms: {
    event_contact: 'Contact me when <strong>{eventname}</strong> photos are ready',
  },
  contact: {
    getnotifications: 'Get Notifications',
    getnotifiedonyourdevice: 'Get notified on your device when your photos are ready',
    cannotsend: 'Unable to send',
  },

  checkout: {
    viewimages: 'View Images',
    digital: 'Digital',
    viewyourotherimages: 'View your other images',
    viewyour: 'View your',
  },
}

deepMerge(enBase, unflatten(enLocale))

/** Lazy import map for non-English locales */
const localeLoaders = {
  fr: () => import('@/locales/fr.json'),
  de: () => import('@/locales/de.json'),
  es: () => import('@/locales/es.json'),
  it: () => import('@/locales/it.json'),
  el: () => import('@/locales/el.json'),
  nl: () => import('@/locales/nl.json'),
  pt: () => import('@/locales/pt.json'),
  da: () => import('@/locales/da.json'),
  nb: () => import('@/locales/nb.json'),
  sv: () => import('@/locales/sv.json'),
  is: () => import('@/locales/is.json'),
  cs: () => import('@/locales/cs.json'),
  ja: () => import('@/locales/ja.json'),
  'zh-CN': () => import('@/locales/zh-CN.json'),
  'zh-HK': () => import('@/locales/zh-HK.json'),
  'en-US': () => import('@/locales/en-US.json'),
}

/** Track which locales have already been loaded */
const loadedLocales = new Set(['en'])

/**
 * Load a locale's messages on demand, then set it as active.
 * Returns immediately if already loaded. English is always available.
 * @param {string} code - ISO locale code
 * @returns {Promise<void>}
 */
async function loadLocale(code) {
  if (loadedLocales.has(code)) {
    i18n.locale = code
    return
  }
  const loader = localeLoaders[code]
  if (!loader) {
    i18n.locale = code
    return
  }
  const messages = await loader()
  i18n.setLocaleMessage(code, unflatten(messages.default || messages))
  loadedLocales.add(code)
  i18n.locale = code
}

const i18n = new VueI18n({
  locale: 'en',
  fallbackLocale: 'en',
  messages: { en: enBase },
  silentTranslationWarn: true,
})

export { languages, loadLocale }
export default i18n
